import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosHeaders, AxiosProgressEvent } from "axios";
import { apiBaseUrl } from "@/environment/environment";
import { ApiErrors, ApiException } from "@/app/shared/services/volateq-api/api-errors";
import { TableFilterRequest } from "./api-requests/common/table-requests";
import { store } from "../../stores/store";
import { TokenResult } from "./api-schemas/auth-schema";
import { waitFor } from "../helper/debounce-helper";
import { themeService } from "../theme-service/theme-service";
import { whiteLabelingService } from "../white-labeling-service/white-labeling-service";

export class HttpClientBase {
  private readonly httpClient: AxiosInstance;
  private reloadingPermissions = false;
  private refreshingToken: Promise<void> | null = null;

  public isUploading = false;

  protected readonly baseURL: string | undefined;

  static getRequestHeader(existingHeaders?: AxiosHeaders): AxiosHeaders {
    const headers = new AxiosHeaders(existingHeaders);
    if (store.auth.isAuthenticated) {
      headers.setAuthorization(`Bearer ${store.auth.token}`);
    }
    if (store.protection.botProtectionToken) {
      headers.set("XVolateqBotProtectionToken", store.protection.botProtectionToken);
    }

    return headers;
  }

  static createAuthHttpClient(baseURL: string): AxiosInstance {
    const httpClient = Axios.create({ baseURL });

    httpClient.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      config.headers = this.getRequestHeader();

      return config;
    });

    return httpClient;
  }

  constructor() {
    this.baseURL = apiBaseUrl;
    this.httpClient = HttpClientBase.createAuthHttpClient(this.baseURL!);
    this.httpClient.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data;
      },
      async (error: AxiosError<ApiException>) => {
        let errResponseData = error.response?.data;
        if (errResponseData) {
          let apiError: string | undefined = errResponseData?.error;
          if (!apiError && error.config?.responseType === "blob" && errResponseData instanceof Blob) {
            try {
              errResponseData = JSON.parse(await errResponseData.text());
              apiError = errResponseData?.error;
            } catch (e) {
              console.warn("Trying to parse error object failed. Could not parse blob as JSON. " + e)
            }
          }
          
          if (apiError && !this.isUploading) {
            if (apiError === ApiErrors.INVALID_TOKEN) {
              store.auth.updateToken({ token: "", permissions: [], customer: undefined, whiteLabelingVersion: undefined, refreshToken: undefined });

              location.reload();
            }
            else if (apiError === ApiErrors.TOKEN_EXPIRED) {
              try {
                await this.refreshAccessToken();

                // retry the original request with the new access token
                error.config!.headers = HttpClientBase.getRequestHeader();
                return (await Axios.request(error.config!)).data;
              } catch {
                store.auth.updateToken({ token: "", permissions: [], customer: undefined, whiteLabelingVersion: undefined, refreshToken: undefined });

                location.reload();
              }
            }
            else if (apiError === ApiErrors.PERMISSION_RELOAD_REQUIRED) {
              // The permissions of the user have changed. REST-API forces us to reload them...
              try {
                await this.reloadPermissions();
  
                // retry the same request again, but with a fresh token
                error.config!.headers = HttpClientBase.getRequestHeader();
                return (await Axios.request(error.config!)).data
              } catch (e) {
                return Promise.reject(e);
              }
            }
          }
          
          return Promise.reject(errResponseData);
        }

        // CORS Error due to 404 of nginx (it does not set cors header...) due to missing or wrong bot protection token
        if (error.response === undefined) {
          try {
            await this.refreshProtectionToken();
            
            // retry the same request again, but with a fresh token
            error.config!.headers = HttpClientBase.getRequestHeader(error.config!.headers);
            return (await Axios.request(error.config!)).data
          } catch {
            console.error("DISCONNECT");
            console.error(error);

            return Promise.reject({
              error: ApiErrors.DISCONNECT,
              message: "No connection to server",
            });
          }
        }

        console.error("FATAL");
        console.error(error);

        return Promise.reject({
          error: ApiErrors.SOMETHING_WENT_WRONG,
          message: "Ooops! Something went horribly wrong!",
        });
      }
    );
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.refreshingToken) {
      return this.refreshingToken;
    }

    this.refreshingToken = (async () => {
      const refreshToken = store.auth.refreshToken;
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response: TokenResult = await Axios.post(
        `${this.baseURL}/auth/token/refresh`,
        { refresh_token: refreshToken },
        { headers: HttpClientBase.getRequestHeader() },
      ).then(r => r.data);

      store.auth.updateToken({
        token: response.token,
        permissions: response.permissions,
        customer: response.customer,
        whiteLabelingVersion: response.white_labeling_version,
        isAdmin: response.is_volateq_admin,
        refreshToken: response.refresh_token || refreshToken,
      });
    })().finally(() => {
      this.refreshingToken = null;
    });

    return this.refreshingToken;
  }

  private async refreshProtectionToken() {
    const response = await Axios.get((new URL('/bot-protection-token', this.baseURL)).toString());
    store.protection.updateBotProtectionToken(response.data)
  }

  private async reloadPermissions() {
    if (this.reloadingPermissions) {
      while (this.reloadingPermissions) {
        await waitFor(500);
      }                
    } else {
      this.reloadingPermissions = true;
      try {
        const tokenResult: TokenResult = (await Axios.post(
          `${this.baseURL}/auth/user/reload-permissions`,
          undefined,
          { headers: HttpClientBase.getRequestHeader() }
        )).data;

        const reloadWhiteLabeling = tokenResult.white_labeling_version !== store.auth.whiteLabelingVersion;
        
        store.auth.updateToken({
          token: tokenResult.token,
          permissions: tokenResult.permissions,
          customer: tokenResult.customer,
          whiteLabelingVersion: tokenResult.white_labeling_version,
          refreshToken: tokenResult.refresh_token,
          ...(tokenResult.is_volateq_admin ? { isAdmin: tokenResult.is_volateq_admin } : {}),
        });

        if (reloadWhiteLabeling) {
          await whiteLabelingService.appendStyles()
        }
      } finally {
        this.reloadingPermissions = false;
      }
    }
  }

  protected async postForm(
    url: string,
    data: Record<string, string | File | File[] | Blob>,
    onUploadProgressEvent?: (progressEvent: AxiosProgressEvent) => void,
    config?: AxiosRequestConfig
  ): Promise<any> {
    const formData = new FormData();

    for (const key in data) {
      const value = data[key];

      if (Array.isArray(value)) {
        for (const item of value) {
          formData.append(key, item);
        }
      } else {
        formData.append(key, value);
      }
    }

    return this.httpClient.post(url, formData, {
      ...config,
      headers: { "Content-Type": "multipart/form-data", ...config?.headers },
      onUploadProgress: onUploadProgressEvent !== undefined ? (progressEvent: AxiosProgressEvent) => {
        onUploadProgressEvent(progressEvent);
      } : undefined
    });
  }

  protected async post(url: string, data?: any, config?: AxiosRequestConfig | undefined, params?: any): Promise<any> {
    return this.httpClient.post(this.getUrl(url, params), data, config);
  }

  protected async get(url: string, params?: any, config?: AxiosRequestConfig | undefined): Promise<any> {
    return this.httpClient.get(this.getUrl(url, params), config);
  }

  /**
   * GET without the session and the bot protection header, for an endpoint that authenticates
   * a request by a token in its URL. Those headers would turn every single request into a CORS
   * preflight, because the endpoint is served by another host than the REST API.
   */
  protected async getWithoutAuthHeaders(url: string, params?: any, config?: AxiosRequestConfig | undefined): Promise<any> {
    return (await Axios.get(this.getUrl(url, params), config)).data;
  }

  protected async put(url: string, data?: any, config?: AxiosRequestConfig | undefined, params?: any): Promise<any> {
    return this.httpClient.put(this.getUrl(url, params), data, config);
  }

  protected async delete(url: string, config?: AxiosRequestConfig | undefined): Promise<any> {
    return this.httpClient.delete(url, config);
  }

  public getUrl(url: string, params?: any) {
    return url + ((params && this.getQueryParams(params)) || "");
  }

  protected getQueryParams(params: any): string {
    return (
      "?" +
      Object.keys(params)
        .map(key => key + "=" + encodeURIComponent(params[key]))
        .join("&")
    );
  }

  protected getEncodedAnalysisResultFilterParams(filterParams?: TableFilterRequest) {
    let encodedFilterParams = "";
    if (filterParams) {
      if (filterParams.filters) {
        encodedFilterParams += `&filters=${encodeURIComponent(JSON.stringify(filterParams.filters))}`;
      }
      if (filterParams.component_filter) {
        encodedFilterParams += `&component_filter=${encodeURIComponent(JSON.stringify(filterParams.component_filter))}`;
      }
      if (filterParams.columns_selection) {
        encodedFilterParams += `&columns_selection=${encodeURIComponent(
          JSON.stringify(filterParams.columns_selection)
        )}`;
      }
      if (filterParams.filter_logic) {
        encodedFilterParams += `&filter_logic=${filterParams.filter_logic}`;
      }
    }

    return encodedFilterParams;
  }
}
